import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import QRCode from 'qrcode';
import './QRShareModal.css';

interface QRShareModalProps {
  beneficiary: any;
  onClose: () => void;
}

const QRShareModal: React.FC<QRShareModalProps> = ({ beneficiary, onClose }) => {
  const [qrCodeUrl, setQrCodeUrl] = useState<string>('');
  const [shareData, setShareData] = useState<any>(null);

  useEffect(() => {
    const generateQRCode = async () => {
      try {
        // Create shareable data object
        const shareableData = {
          id: beneficiary.id,
          name: beneficiary.name,
          accountNumber: beneficiary.accountNumber,
          trustLevel: beneficiary.trustLevel,
          timestamp: new Date().toISOString(),
          sharedBy: 'current-user' // In production, get from auth context
        };

        setShareData(shareableData);

        // Generate QR code
        const qrData = JSON.stringify(shareableData);
        const qrCodeDataUrl = await QRCode.toDataURL(qrData, {
          width: 256,
          margin: 2,
          color: {
            dark: '#1e293b',
            light: '#f8fafc'
          }
        });

        setQrCodeUrl(qrCodeDataUrl);
      } catch (error) {
        console.error('Error generating QR code:', error);
      }
    };
    generateQRCode();
  }, [beneficiary]);

  const copyToClipboard = () => {
    if (shareData) {
      navigator.clipboard.writeText(JSON.stringify(shareData, null, 2));
      // Show toast notification
    }
  };

  const downloadQR = () => {
    if (qrCodeUrl) {
      const link = document.createElement('a');
      link.download = `beneficiary-${beneficiary.name.replace(/\s+/g, '-')}-qr.png`;
      link.href = qrCodeUrl;
      link.click();
    }
  };

  return (
    <motion.div
      className="modal-overlay"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      onClick={onClose}
    >
      <motion.div
        className="modal-content"
        initial={{ scale: 0.8, y: 50 }}
        animate={{ scale: 1, y: 0 }}
        exit={{ scale: 0.8, y: 50 }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="modal-header">
          <h2>Share Beneficiary</h2>
          <button className="close-btn" onClick={onClose}>×</button>
        </div>

        <div className="qr-share-content">
          <div className="beneficiary-preview">
            <div className="preview-avatar">
              {beneficiary.name.split(' ').map((n: string) => n[0]).join('').toUpperCase()}
            </div>
            <div className="preview-info">
              <h4>{beneficiary.name}</h4>
              <p>{beneficiary.accountNumber}</p>
            </div>
          </div>

          <div className="qr-section">
            <h4>QR Code</h4>
            <div className="qr-container">
              {qrCodeUrl ? (
                <img src={qrCodeUrl} alt="QR Code" className="qr-image" />
              ) : (
                <div className="qr-loading">Generating QR Code...</div>
              )}
            </div>
            <p className="qr-description">
              Scan this QR code to import beneficiary details
            </p>
          </div>

          <div className="share-actions">
            <button className="action-btn secondary" onClick={copyToClipboard}>
              📋 Copy Data
            </button>
            <button className="action-btn primary" onClick={downloadQR}>
              💾 Download QR
            </button>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
};

export default QRShareModal;
